import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  ClassSelector,
  CustomFormValidators,
  EcommerceTrackingService,
  ScrollService,
  WindowRef,
} from '@boels/helpers';
import {
  CmsComponentData,
  GDPRMessageComponentData,
  GlobalMessageType,
  TrackingFormSubmitStatus,
  TrackingFormType,
} from '@boels/models';
import {
  CreditAccountDTO,
  CreditAccountFacade,
  GlobalMessageFacade,
  UserProfileFacade,
} from '@boels/shared-state';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { NgSelectModule } from '@ng-select/ng-select';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { catchError, filter, first, map, of, take, tap } from 'rxjs';
import { FormErrorsComponent } from '../form/form-errors/form-errors.component';
import { TextInputComponent } from '../form/input/text-input.component';
import { PhoneComponent } from '../phone/phone.component';
import { PrivacyStatementLinkComponent } from '../form/privacy-statement-link/privacy-statement-link.component';

@Component({
  selector: 'boels-rent-on-account-form',
  templateUrl: './rent-on-account-form.component.html',
  styleUrls: ['./rent-on-account-form.component.scss'],
  host: { ngSkipHydration: 'true' },
  imports: [
    TranslocoModule,
    ReactiveFormsModule,
    CommonModule,
    NgSelectModule,
    FormErrorsComponent,
    TextInputComponent,
    PhoneComponent,
    FontAwesomeModule,
    PrivacyStatementLinkComponent,
  ],
  providers: [
    CreditAccountFacade,
    ScrollService,
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['checkout', 'form'],
    },
  ],
})
export class RentOnAccountFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private userService = inject(UserProfileFacade);
  private globalMessageService = inject(GlobalMessageFacade);
  private scrollService = inject(ScrollService);
  private elementRef = inject(ElementRef);
  private winRef = inject(WindowRef);
  private creditAccountFacade = inject(CreditAccountFacade);
  private ecommerceTrackingService = inject(EcommerceTrackingService);

  @Input() componentData: CmsComponentData<GDPRMessageComponentData>;
  @ViewChild('submitButton') submitButton: ElementRef<HTMLButtonElement>;

  countries$ = this.userService.getCountries();
  titles$ = this.userService.getTitles();

  submitted = false;
  faChevronRightIcon = faChevronRight;

  creditAccountForm: FormGroup = this.fb.group({
    companyName: ['', Validators.required],
    vat: ['', Validators.required],
    title: [null, Validators.required],
    firstName: ['', Validators.required],
    surname: ['', Validators.required],
    address: ['', Validators.required],
    postcode: ['', Validators.required],
    city: ['', Validators.required],
    country: [null, Validators.required],
    telephone: ['', Validators.required],
    email: ['', [Validators.required, CustomFormValidators.emailValidator]],
  });

  get country(): FormControl {
    return this.creditAccountForm.controls['country'] as FormControl;
  }

  get telephone(): FormControl {
    return this.creditAccountForm.controls['telephone'] as FormControl;
  }

  ngOnInit(): void {
    this.initCountry();
  }

  submitForm(): void {
    this.submitted = true;

    if (this.creditAccountForm.valid) {
      this.sendForm();
    } else {
      this.handleErrors();
    }
  }

  private sendForm() {
    this.creditAccountFacade
      .submitForm(this.mapValues())
      .pipe(
        map(() => ({
          message: {
            key: 'customerContactForm.submitSuccess',
            scope: 'form',
          },
          type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
        })),
        tap(() => {
          this.ecommerceTrackingService.setFormSubmitEvent(
            TrackingFormType.CREDIT_ACCOUNT_APPLICATION,
            TrackingFormSubmitStatus.SUCCESS,
          );
          this.resetForm();
        }),
        catchError(() => {
          this.ecommerceTrackingService.setFormSubmitEvent(
            TrackingFormType.CREDIT_ACCOUNT_APPLICATION,
            TrackingFormSubmitStatus.FAIL,
          );
          return of({
            message: { key: 'customerContactForm.submitError', scope: 'form' },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        }),
        take(1),
      )
      .subscribe(({ message, type }) =>
        this.globalMessageService.add(message, type),
      );

    this.winRef.nativeWindow?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private mapValues(): CreditAccountDTO {
    return {
      ...this.creditAccountForm.value,
      telephone: this.telephone.value.e164Number,
    };
  }

  private handleErrors() {
    this.creditAccountForm.markAllAsTouched();
    this.submitButton.nativeElement.focus();
    this.scrollService.scrollToElement(
      ClassSelector.INVALID_FORM_INPUT,
      this.elementRef,
    );
  }

  private resetForm(): void {
    this.submitted = false;
    this.creditAccountForm.reset();
    this.initCountry();
  }

  private initCountry(): void {
    this.countries$
      .pipe(
        map((details) => details.countries),
        filter((countries) => Boolean(countries?.length)),
        first(),
      )
      .subscribe((countries) =>
        this.country.patchValue(countries?.at(0)?.isocode),
      );
  }
}
